/**
 * ManualTeamsCard + Add/Edit sheets — parent-side self-add for the
 * Family Sports Dashboard.
 *
 * Lets a Clipboard+ guardian add a team that their coach hasn't put on
 * ChalkOS yet. Once added, the team's events show up in the same
 * buckets + iCal feed as coach-driven teams.
 *
 * Includes the "Tell their coach about ChalkOS" referral CTA — viral
 * acquisition surface. When the parent fills in the absent coach's
 * email, the card surfaces a one-tap referral button that fires a
 * friendly templated email asking the coach to give ChalkOS a look.
 */
import { useEffect, useState } from "react";
import { CalendarPlus, Megaphone, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton, ChalkCard } from "./primitives";

const SPORTS = [
  ["other", "Other"],
  ["baseball", "Baseball"],
  ["softball", "Softball"],
  ["soccer", "Soccer"],
  ["basketball", "Basketball"],
  ["football", "Football"],
];

const EVENT_TYPES = [
  ["practice", "Practice"],
  ["game", "Game"],
  ["scrimmage", "Scrimmage"],
  ["team_event", "Team event"],
  ["other", "Other"],
];

export default function ManualTeamsCard({ onChanged }) {
  const [teams, setTeams] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // team or null
  const [eventTeam, setEventTeam] = useState(null);

  async function load() {
    try {
      const d = await api.manualTeams();
      setTeams(d.teams || []);
    } catch (e) {
      // Pickup parents will hit the tier gate — surface a soft empty,
      // not a toast (we have an upsell ribbon for that case).
      setTeams([]);
    }
  }
  useEffect(() => { load(); }, []);

  if (teams === null) {
    return null;
  }

  async function deleteTeam(team) {
    if (!window.confirm(`Remove ${team.name} and everything inside it?`)) return;
    try {
      await api.deleteManualTeam(team.id);
      toast.success("Removed.");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.message || "Couldn't remove team.");
    }
  }

  async function inviteCoach(team) {
    if (!team.coach_email) {
      toast.error("Add their coach's email on the team first.");
      return;
    }
    try {
      await api.inviteCoachToChalkOS({ team_id: team.id });
      toast.success(`Invite sent to ${team.coach_email}.`);
    } catch (e) {
      toast.error(e.message || "Couldn't send invite.");
    }
  }

  return (
    <>
      <ChalkCard data-testid="manual-teams-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: teams.length ? 12 : 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--chalk-board)" }}>
              Your other teams
            </div>
            <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2, lineHeight: 1.4 }}>
              Add a team whose coach isn't on Chalk OS yet — we'll keep
              its schedule in here with everyone else's.
            </div>
          </div>
          <button
            type="button"
            data-testid="manual-team-add"
            onClick={() => setAddOpen(true)}
            aria-label="Add a team"
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: "var(--chalk-board)", color: "#F5F0E8",
              border: "none", cursor: "pointer", display: "inline-flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <Plus size={18} aria-hidden="true" />
          </button>
        </div>
        {teams.map((t) => (
          <div
            key={t.id}
            data-testid={`manual-team-${t.id}`}
            style={{
              borderTop: "0.5px solid var(--chalk-border)", paddingTop: 10,
              marginTop: 10, display: "flex", flexDirection: "column", gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{
                width: 6, height: 36, borderRadius: 3,
                background: t.color || "#2E7D52", flexShrink: 0,
              }} aria-hidden="true" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--chalk-board)" }}>
                  {t.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {(t.sport || "other")} · {t.athletes?.length || 0} kid{(t.athletes?.length || 0) === 1 ? "" : "s"}
                  {t.coach_name ? ` · Coach ${t.coach_name}` : ""}
                </div>
              </div>
              <button
                type="button"
                aria-label={`Delete ${t.name}`}
                data-testid={`manual-team-delete-${t.id}`}
                onClick={() => deleteTeam(t)}
                style={{
                  background: "transparent", border: "none",
                  color: "var(--chalk-muted)", cursor: "pointer", padding: 6,
                }}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <SmallChip
                testId={`manual-team-add-event-${t.id}`}
                icon={<CalendarPlus size={12} aria-hidden="true" />}
                label="Add event"
                onClick={() => setEventTeam(t)}
              />
              <SmallChip
                testId={`manual-team-edit-${t.id}`}
                icon={<Users size={12} aria-hidden="true" />}
                label="Edit"
                onClick={() => setEditing(t)}
              />
              {t.coach_email && (
                <SmallChip
                  testId={`manual-team-invite-${t.id}`}
                  icon={<Megaphone size={12} aria-hidden="true" />}
                  label="Tell their coach"
                  onClick={() => inviteCoach(t)}
                />
              )}
            </div>
          </div>
        ))}
      </ChalkCard>

      <ManualTeamSheet
        isOpen={addOpen}
        onDismiss={() => setAddOpen(false)}
        onSaved={() => { load(); onChanged && onChanged(); }}
      />
      <ManualTeamSheet
        isOpen={!!editing}
        team={editing}
        onDismiss={() => setEditing(null)}
        onSaved={() => { load(); onChanged && onChanged(); }}
      />
      <ManualEventSheet
        isOpen={!!eventTeam}
        team={eventTeam}
        onDismiss={() => setEventTeam(null)}
        onSaved={() => { load(); onChanged && onChanged(); }}
      />
    </>
  );
}

function SmallChip({ icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "5px 10px", borderRadius: 99,
        background: "transparent",
        border: "0.5px solid var(--chalk-border)",
        color: "var(--green-mid)", fontWeight: 700, fontSize: 11,
        letterSpacing: "0.04em", textTransform: "uppercase",
        cursor: "pointer",
      }}
    >
      {icon} {label}
    </button>
  );
}

// ── Add / Edit team sheet ────────────────────────────────────────

function ManualTeamSheet({ isOpen, team, onDismiss, onSaved }) {
  const isEdit = !!team;
  const [name, setName] = useState("");
  const [sport, setSport] = useState("other");
  const [coachName, setCoachName] = useState("");
  const [coachEmail, setCoachEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && team) {
      setName(team.name || "");
      setSport(team.sport || "other");
      setCoachName(team.coach_name || "");
      setCoachEmail(team.coach_email || "");
    } else if (isOpen && !team) {
      setName(""); setSport("other"); setCoachName(""); setCoachEmail("");
    }
  }, [isOpen, team]);

  async function submit() {
    if (!name.trim()) {
      toast.error("Give the team a name.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        sport,
        coach_name: coachName.trim() || null,
        coach_email: coachEmail.trim() || null,
      };
      if (isEdit) {
        await api.updateManualTeam(team.id, body);
        toast.success("Team updated.");
      } else {
        await api.createManualTeam(body);
        toast.success("Team added.");
      }
      onSaved && onSaved();
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title={isEdit ? "Edit team" : "Add a team"} onDismiss={onDismiss}>
      <div data-testid="manual-team-sheet" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Team name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sam's Soccer Stars"
            data-testid="manual-team-name"
            style={_input}
          />
        </Field>
        <Field label="Sport">
          <select
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            data-testid="manual-team-sport"
            style={_input}
          >
            {SPORTS.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
          </select>
        </Field>
        <Field label="Their coach's name (optional)">
          <input
            value={coachName}
            onChange={(e) => setCoachName(e.target.value)}
            placeholder="Coach Mike"
            data-testid="manual-team-coach-name"
            style={_input}
          />
        </Field>
        <Field label="Their coach's email (optional)">
          <input
            type="email"
            value={coachEmail}
            onChange={(e) => setCoachEmail(e.target.value)}
            placeholder="coach@example.com"
            data-testid="manual-team-coach-email"
            style={_input}
          />
          <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 4 }}>
            Add this and you'll be able to send the coach a one-tap invite
            to try Chalk OS — handy if you want everyone on the same app.
          </div>
        </Field>
        <ChalkActionButton
          testId="manual-team-save"
          variant="primary"
          label={saving ? "Saving…" : (isEdit ? "Save changes" : "Add team")}
          icon={<Plus size={16} aria-hidden="true" />}
          loading={saving}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}

// ── Add event sheet ──────────────────────────────────────────────

function ManualEventSheet({ isOpen, team, onDismiss, onSaved }) {
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState("practice");
  const [date, setDate] = useState(_today());
  const [time, setTime] = useState("17:00");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(""); setEventType("practice"); setDate(_today());
      setTime("17:00"); setLocation("");
    }
  }, [isOpen]);

  async function submit() {
    if (!team) return;
    if (!title.trim()) {
      toast.error("Give the event a title.");
      return;
    }
    setSaving(true);
    try {
      await api.createManualEvent({
        team_id: team.id,
        title: title.trim(),
        event_type: eventType,
        start_local: `${date}T${time}`,
        location: location.trim() || null,
      });
      toast.success("Added to your calendar.");
      onSaved && onSaved();
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't add event.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title={`Add to ${team?.name || "team"}`} onDismiss={onDismiss}>
      <div data-testid="manual-event-sheet" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="What is it?">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Tuesday practice"
            data-testid="manual-event-title"
            style={_input}
          />
        </Field>
        <Field label="Type">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            data-testid="manual-event-type"
            style={_input}
          >
            {EVENT_TYPES.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
          </select>
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 8 }}>
          <Field label="Date">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="manual-event-date"
              style={_input}
            />
          </Field>
          <Field label="Time">
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="manual-event-time"
              style={_input}
            />
          </Field>
        </div>
        <Field label="Location (optional)">
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Field 4"
            data-testid="manual-event-location"
            style={_input}
          />
        </Field>
        <ChalkActionButton
          testId="manual-event-save"
          variant="primary"
          label={saving ? "Saving…" : "Add to calendar"}
          icon={<Plus size={16} aria-hidden="true" />}
          loading={saving}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
        color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 6,
      }}>{label}</div>
      {children}
    </label>
  );
}

const _input = {
  width: "100%", border: "0.5px solid var(--chalk-border)",
  borderRadius: 10, padding: "11px 12px", fontSize: 16,
  background: "#FBF8F2", color: "var(--chalk-board)", outline: "none",
  fontFamily: "inherit",
};

function _today() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
